import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy,
  PLATFORM_ID, ViewChild, ViewEncapsulation
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Subscription, fromEvent, interval } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { TranslateModule } from '@ngx-translate/core';

import { SdButtonComponent, SdLoaderComponent } from '@seb/web-design-system/core';

import type { IVideoSequencer } from './video-sequencer.interface';

@Component({
  selector: 'sd-video-sequencer',
  templateUrl: './video-sequencer.component.html',
  styleUrls: ['./video-sequencer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdLoaderComponent, SdButtonComponent, TranslateModule]
})
export class SdVideoSequencerComponent implements AfterViewInit, OnDestroy {
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private cdRef: ChangeDetectorRef,
  ) {}

  isPrevBtnDisabled = true;
  isNextBtnDisabled = false;
  currentCuePoint = 0;
  intervalRewind = interval(60);
  intervalRewindSubscription: Subscription;
  video;
  isVideoRewind = false;
  videoTimeUpdateListener: Subscription;
  isLoading = true;
  descriptionUUID: string = 'videoSequencer-' + uuid();
  ariaLiveState = 'off';

  @Input()
  videoSequencer: IVideoSequencer;

  @Input()
  context = 'Video sequencer';

  @ViewChild('videoElement')
  videoElement: ElementRef;

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.video = this.videoElement.nativeElement;
      this.video.muted = true;
      this.video.currentTime =
        this.videoSequencer.cuePoints[this.currentCuePoint].time;
      this.videoTimeUpdateListener = fromEvent(
        this.video,
        'timeupdate',
      ).subscribe(() => {
        this.videoReachedCuePoint();
        this.cdRef.markForCheck();
      });
      /* A11Y */
      this.ariaLiveState = 'polite';
    }
  }

  ngOnDestroy() {
    this.videoTimeUpdateListener?.unsubscribe();
    this.intervalRewindSubscription?.unsubscribe();
  }

  clickPrevBtn(isDisabled: boolean) {
    if (!isDisabled) {
      this.isVideoRewind = true;
      this.currentCuePoint =
        this.currentCuePoint > 0
          ? this.currentCuePoint - 1
          : this.videoSequencer.cuePoints.length - 1;
      this.isPrevBtnDisabled = this.isNextBtnDisabled = true;
      this.reverseVideo();
    }
  }

  clickNextBtn(isDisabled: boolean) {
    if (!isDisabled) {
      this.isVideoRewind = false;
      this.currentCuePoint =
        this.currentCuePoint < this.videoSequencer.cuePoints.length - 1
          ? this.currentCuePoint + 1
          : 0;
      this.isPrevBtnDisabled = this.isNextBtnDisabled = true;
      this.video.play();
    }
  }

  checkBtnState() {
    this.isPrevBtnDisabled = this.currentCuePoint >= 1 ? false : true;
    this.isNextBtnDisabled =
      this.currentCuePoint < this.videoSequencer.cuePoints.length - 1
        ? false
        : true;
  }

  videoReachedCuePoint() {
    if (this.isVideoRewind) {
      if (
        this.video.currentTime <=
        this.videoSequencer.cuePoints[this.currentCuePoint].time
      ) {
        this.intervalRewindSubscription?.unsubscribe();
        this.video.pause();
        this.checkBtnState();
      }
    } else {
      if (
        this.video.currentTime >=
        this.videoSequencer.cuePoints[this.currentCuePoint].time
      ) {
        this.video.pause();
        this.checkBtnState();
      }
    }
  }

  reverseVideo() {
    this.intervalRewindSubscription = this.intervalRewind.subscribe(() => {
      this.video.currentTime += -0.05;
      this.cdRef.markForCheck();
    });
  }

  goToCuePoint(cuePointIndex: number) {
    if (cuePointIndex !== this.currentCuePoint) {
      this.currentCuePoint = cuePointIndex;
      this.video.currentTime =
        this.videoSequencer.cuePoints[this.currentCuePoint].time;
      this.video.pause();
    }
  }

  videoIsLoaded() {
    this.isLoading = false;
  }
}
